import React, { useCallback, useEffect, useRef, useState } from "react";
import { Range } from "react-range";

/**
 * MultiRange + SingleRange
 * Single-file React component (no Tailwind, inline styles)
 * - MultiRange uses `react-range` (dual-thumb)
 * - SingleRange uses a native <input type="range"> with a gradient-filled track
 *
 * Install (if using MultiRange's dependency):
 * npm install react-range
 *
 * Usage:
 * import { MultiRange, SingleRange } from './MultiRange.jsx';
 *
 * <MultiRange min={0} max={100} step={1} initial={[15,70]} onChange={(v)=>console.log(v)} />
 * <SingleRange min={0} max={100} step={1} initial={40} onChange={(v)=>console.log(v)} />
 */

const DEFAULT_TRACK_HEIGHT = 8;

function clampBetween(v, min, max) {
  return Math.min(Math.max(v, min), max);
}

function toPercent(value, min, max) {
  return ((value - min) / (max - min)) * 100;
}

/** MultiRange (react-range dual thumb) */
export function MultiRange({
  min = 0,
  max = 100,
  step = 1,
  initial = [20, 80],
  lockedValue = 0,
  onChange = () => {},
  showValues = true,
}) {
  const fixedLower = clampBetween(lockedValue, min, max);
  const minDistance = Math.max(step, 1);

  const initialUpper = clampBetween(initial[1] ?? fixedLower + minDistance, min, max);
  const initialUpperClamped = Math.max(initialUpper, fixedLower + step);

  const [values, setValues] = useState(() => [fixedLower, initialUpperClamped]);

  useEffect(() => {
    setValues((prev) => {
      const prevUpper = clampBetween(prev[1] ?? initialUpperClamped, min, max);
      const newUpper = Math.max(prevUpper, fixedLower + minDistance);
      const out = [fixedLower, newUpper];

      onChange(out);
      return out;
    });
  }, [fixedLower, min, max, minDistance]);

  // const fixedLower = clampBetween(lockedValue, min, max);
  // const minDistance = Math.max(step, 1);

  // const [values, setValues] = useState(() => {
  //   const upperInit = clampBetween(initial[1] ?? fixedLower + minDistance, min, max);
  //   // ensure upper at least fixedLower + step
  //   const upperClamped = Math.max(upperInit, fixedLower + step);
  //   return [fixedLower, upperClamped];

  //   // const a = clampBetween(initial[0], min, max);
  //   // const b = clampBetween(initial[1], min, max);
  //   // return a <= b ? [a, b] : [b, a];
  // });

  // const minDistance = Math.max(step, 1);

  const handleChange = useCallback(
    (v) => {
      // v is an array [maybeA, maybeB] — only use maybeB as new upper
      let newUpper = clampBetween(Math.round(v[1] / step) * step, min, max);
      // make sure upper stays >= fixedLower + minDistance
      newUpper = Math.max(newUpper, fixedLower + minDistance);
      const out = [fixedLower, newUpper];
      setValues(out);
      onChange(out);
    },
    [fixedLower, min, max, step, minDistance, onChange]
  );

  // const handleChange = useCallback(
  //   (v) => {
  //     setValues((prev) => {
  //       let [oldA, oldB] = prev;
  //       let [newA, newB] = v;

  //       if (newA >= newB) {
  //         if (newA !== oldA && newB === oldB) {
  //           newA = clampBetween(newB - minDistance, min, max - minDistance);
  //         } else if (newB !== oldB && newA === oldA) {
  //           newB = clampBetween(newA + minDistance, min + minDistance, max);
  //         } else {
  //           newA = oldA;
  //           newB = oldB;
  //         }
  //       }

  //       newA = clampBetween(Math.round(newA / step) * step, min, max);
  //       newB = clampBetween(Math.round(newB / step) * step, min, max);

  //       const out = [newA, newB];
  //       onChange(out);
  //       return out;
  //     });
  //   },
  //   [min, max, minDistance, step, onChange]
  // );

  const leftPct = toPercent(values[0], min, max);
  const rightPct = toPercent(values[1], min, max);

  const trackBackground = `linear-gradient(90deg, var(--range-track-bg) 0%, var(--range-track-bg) ${leftPct}%, var(--range-track-progress-bg) ${leftPct}% ${rightPct}%, var(--range-track-bg) ${rightPct}% 100%)`;

  const containerStyle = { width: "100%", maxWidth: 640, fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial" };
  const valuesRowStyle = { display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 14, color: "#374151" };
  const sliderWrapStyle = { position: "relative", padding: "12px 8px" };
  const trackStyle = { height: `${DEFAULT_TRACK_HEIGHT}px`, borderRadius: 9999, background: trackBackground, display: "flex", alignItems: "center", padding: "0 6px" };
  const floatingLabel = (pct) => ({ position: "absolute", top: -26, left: `${pct}%`, transform: "translateX(-50%)", background: "var(--range-value-bg)", padding: "2px 8px", borderRadius: 6, boxShadow: "0 4px 10px rgba(2,6,23,0.08)", fontSize: 12, color: "var(--range-value-color)", whiteSpace: "nowrap" });
  const thumbBaseStyle = { height: 28, width: 28, borderRadius: 9999, background: "var(--range-thumb-bg)", border: "3px solid var(--range-track-progress-bg)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 10px rgba(2,6,23,0.08)" };
  const ticksRowStyle = { marginTop: 12, display: "flex", justifyContent: "space-between", fontSize: 12, color: "#6b7280", padding: "0 4px" };

  return (
    <div style={containerStyle}>
      {/* <div style={valuesRowStyle}>
        <div>Min: {values[0]}</div>
        <div>Max: {values[1]}</div>
      </div> */}

      <div style={sliderWrapStyle}>
        {showValues && (
          <>
            <div style={floatingLabel(leftPct)}>{values[0]}</div>
            <div style={floatingLabel(rightPct)}>{values[1]}</div>
          </>
        )}

        <div style={trackStyle}>
          <Range
            values={values}
            step={step}
            min={min}
            max={max}
            onChange={handleChange}
            renderTrack={({ props, children }) => (
              <div onMouseDown={props.onMouseDown} onTouchStart={props.onTouchStart} style={{ ...props.style, width: "100%" }}>
                <div ref={props.ref} style={{ height: `${DEFAULT_TRACK_HEIGHT}px` }}>
                  {children}
                </div>
              </div>
            )}
            renderThumb={({ index, props: thumbProps, isDragged }) => (
              <div {...thumbProps} role="slider" aria-label={index === 0 ? "Minimum thumb" : "Maximum thumb"} aria-valuemin={min} aria-valuemax={max} aria-valuenow={values[index]} style={{ ...thumbProps.style, ...thumbBaseStyle, boxShadow: isDragged ? "0 6px 18px rgba(13, 110, 253, 0.18)" : thumbBaseStyle.boxShadow }}>
                {/* <div style={{ width: 6, height: 6, borderRadius: 3, background: "#0f172a" }} /> */}
              </div>
            )}
          />
        </div>

        <div className="goal-ticks" style={ticksRowStyle}>
          <span>{min}</span>
          <span>{Math.round(min + (max - min) * 0.25)}</span>
          <span>{Math.round(min + (max - min) * 0.5)}</span>
          <span>{Math.round(min + (max - min) * 0.75)}</span>
          <span>{max}</span>
        </div>
      </div>
    </div>
  );
}

/** SingleRange - native input with gradient-filled track */
export function SingleRange({ min = 0, max = 100, step = 1, initial = 40, value: controlledValue, onChange = () => {}, showValue = true }) {
  const [value, setValue] = useState(() => clampBetween(controlledValue ?? initial, min, max));
  const ref = useRef(null);

  useEffect(() => {
    if (controlledValue !== undefined) {
      setValue(clampBetween(controlledValue, min, max));
    }
  }, [controlledValue, min, max]);

  useEffect(() => {
    if (ref.current) {
      const pct = toPercent(value, min, max);
      ref.current.style.setProperty("--pct", `${pct}%`);
    }
  }, [value, min, max]);

  const handle = (e) => {
    const v = Number(e.target.value);
    if (controlledValue !== undefined) {
      // controlled mode - ask parent to change
      onChange(v);
    } else {
      // uncontrolled - update local and notify
      setValue(v);
      onChange(v);
    }
  };

  const containerStyle = { width: "100%", maxWidth: 640, fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial" };
  const labelRow = { display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 14, color: "#374151" };
  const sliderContainer = { position: "relative", padding: "12px 8px" };
  const inputStyle = {
    WebkitAppearance: "none",
    appearance: "none",
    width: "100%",
    height: 8,
    borderRadius: 9999,
    background: "linear-gradient(90deg, var(--range-track-progress-bg) 0%, var(--range-track-progress-bg) var(--pct, 40%), var(--range-track-bg) var(--pct, 40%) 100%)",
    outline: "none",
  };

  const thumbStyle = {
    WebkitAppearance: "none",
    appearance: "none",
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "var(--range-thumb-bg)",
    border: "3px solid var(--range-track-progress-bg)",
    boxShadow: "0 4px 10px rgba(2,6,23,0.08)",
  };

  const valueBadge = (pct) => ({ position: "absolute", top: -16, left: `${pct}%`, transform: "translateX(-50%)", background: "var(--range-value-bg)", padding: "2px 8px", borderRadius: 6, boxShadow: "0 4px 10px rgba(2,6,23,0.08)", fontSize: 12, color: "var(--range-value-color)" });
  const ticksRowStyle = { marginTop: 12, display: "flex", justifyContent: "space-between", fontSize: 12, color: "#6b7280", padding: "0 4px" };

  const pct = toPercent(value, min, max);

  return (
    <div style={containerStyle}>
      {/* <div style={labelRow}>
        <div>Value: {value}</div>
        <div>
          <small>{min}</small>
        </div>
      </div> */}

      <div style={sliderContainer} ref={ref}>
        {showValue && <div style={valueBadge(pct)}>{value}</div>}

        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handle}
          style={inputStyle}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
        />

        {/* Browser-specific thumb styling via style tag inserted inline */}
        <style>{`
          input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: ${thumbStyle.width}px; height: ${thumbStyle.height}px; border-radius: 50%; background: ${thumbStyle.background}; border: ${thumbStyle.border}; box-shadow: ${thumbStyle.boxShadow}; }
          input[type=range]::-moz-range-thumb { width: ${thumbStyle.width}px; height: ${thumbStyle.height}px; border-radius: 50%; background: ${thumbStyle.background}; border: ${thumbStyle.border}; box-shadow: ${thumbStyle.boxShadow}; }
          input[type=range]::-ms-thumb { width: ${thumbStyle.width}px; height: ${thumbStyle.height}px; border-radius: 50%; background: ${thumbStyle.background}; border: ${thumbStyle.border}; box-shadow: ${thumbStyle.boxShadow}; }
        `}</style>

        <div className="goal-ticks" style={ticksRowStyle}>
          <span>{min}</span>
          <span>{Math.round(min + (max - min) * 0.25)}</span>
          <span>{Math.round(min + (max - min) * 0.5)}</span>
          <span>{Math.round(min + (max - min) * 0.75)}</span>
          <span>{max}</span>
        </div>
      </div>
    </div>
  );
}

/** Demo showing both components */
// export default function RangeDemo() {
//   const [single, setSingle] = useState(40);
//   const [multi, setMulti] = useState([15, 70]);

//   return (
//     <div style={{ padding: 18 }}>
//       <h3>Single Range (native)</h3>
//       <SingleRange min={0} max={100} initial={single} onChange={(v) => setSingle(v)} />

//       <hr style={{ margin: "24px 0" }} />

//       <h3>Multi Range (react-range)</h3>
//       <MultiRange min={0} max={100} initial={multi} onChange={(v) => setMulti(v)} />

//       <div style={{ marginTop: 12 }}>
//         <strong>Single value:</strong> {single}
//         <br />
//         <strong>Multi values:</strong> {multi[0]} - {multi[1]}
//       </div>
//     </div>
//   );
// }
